/**
 * Valuation Forward Point Curves Page
 * Upload, view, and manage forward point curves (bid/ask) for Forward MtM valuation.
 * Uses a two-step upload flow: Preview (parse) → Confirm (store).
 * KAN-118
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactEcharts from 'echarts-for-react';
import { useAuth } from '../../components/auth/AuthContext';
import { valuationService } from '../../services/api/valuationService';
import {
  FwdPointCurveSummary,
  FwdPointCurveData,
  FwdPointCurvePreviewResult,
  FwdPointCurveConfirmResult,
} from '../../types/valuation';
import Toast from '../../components/common/Toast';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../styles/App.css';

/** Format YYYY-MM-DD → DD/MM/YYYY for display */
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '\u2014';
  const parts = dateStr.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

/** Format ISO datetime → DD/MM/YYYY HH:mm */
const formatDateTime = (isoStr: string): string => {
  if (!isoStr) return '\u2014';
  try {
    const d = new Date(isoStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return isoStr;
  }
};

interface ValuationFwdPointCurvesProps {
  /** System date (YYYY-MM-DD) — used as default valuation date (user can override) */
  systemDate?: string;
}

const ValuationFwdPointCurves: React.FC<ValuationFwdPointCurvesProps> = ({ systemDate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  // Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [valuationDate, setValuationDate] = useState(systemDate || new Date().toISOString().split('T')[0]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preview/Confirm state (two-step upload)
  const [previewData, setPreviewData] = useState<FwdPointCurvePreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [uploadResult, setUploadResult] = useState<FwdPointCurveConfirmResult | null>(null);

  // All curves (unfiltered) for deriving available dates
  const [allCurves, setAllCurves] = useState<FwdPointCurveSummary[]>([]);
  const [loadingCurves, setLoadingCurves] = useState(false);
  const [filterDate, setFilterDate] = useState<string>('');

  // Collapsible pair sections
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Selected curve for visualization
  const [selectedCurve, setSelectedCurve] = useState<FwdPointCurveData | null>(null);
  const [loadingCurve, setLoadingCurve] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Overwrite confirmation modal
  const [overwriteModal, setOverwriteModal] = useState<{
    pairs: string;
    date: string;
  } | null>(null);

  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState<{
    pairKey: string;
    date: string;
  } | null>(null);

  // ============================================
  // Derived: available dates, filtered curves, grouped curves
  // ============================================

  const availableDates = useMemo(() => {
    const dates = [...new Set(allCurves.map(c => c.valuationDate))];
    return dates.sort().reverse();
  }, [allCurves]);

  const filteredCurves = useMemo(() => {
    if (!filterDate) return allCurves;
    return allCurves.filter(c => c.valuationDate === filterDate);
  }, [allCurves, filterDate]);

  /** Group filtered curves by pairKey for collapsible sections */
  const groupedCurves = useMemo(() => {
    const groups: Record<string, FwdPointCurveSummary[]> = {};
    filteredCurves.forEach(curve => {
      if (!groups[curve.pairKey]) groups[curve.pairKey] = [];
      groups[curve.pairKey].push(curve);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredCurves]);

  const toggleSection = (pairKey: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(pairKey)) next.delete(pairKey);
      else next.add(pairKey);
      return next;
    });
  };

  // ============================================
  // Load all curves on mount
  // ============================================

  const loadAllCurves = useCallback(async () => {
    if (!clientId) return;
    setLoadingCurves(true);
    try {
      const response = await valuationService.getFwdPointCurves(clientId);
      setAllCurves(response.data || []);
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.fwdPointCurves.loadError'), type: 'error' });
    } finally {
      setLoadingCurves(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    if (clientId) loadAllCurves();
  }, [clientId, loadAllCurves]);

  // ============================================
  // Drag-and-drop handlers
  // ============================================

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const name = file.name.toLowerCase();
      if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv')) {
        setSelectedFile(file);
        setPreviewData(null);
        setUploadResult(null);
      } else {
        setToast({ message: t('valuation.fwdPointCurves.invalidFileType'), type: 'error' });
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setPreviewData(null);
    setUploadResult(null);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ============================================
  // Step 1: Preview (parse file, no storage)
  // ============================================

  const handlePreview = useCallback(async () => {
    if (!selectedFile || !clientId) return;
    setPreviewLoading(true);
    setPreviewData(null);
    setUploadResult(null);
    try {
      const response = await valuationService.previewFwdPointCurves(clientId, selectedFile);
      const data = response.data;
      setPreviewData(data);
      if (data.curves.length === 0) {
        setToast({ message: t('valuation.fwdPointCurves.noCurvesFound'), type: 'error' });
      }
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.fwdPointCurves.previewError'), type: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedFile, clientId, t]);

  // Auto-preview when file is selected
  useEffect(() => {
    if (selectedFile) handlePreview();
  }, [selectedFile, handlePreview]);

  // ============================================
  // Step 2: Confirm (store curves)
  // ============================================

  const executeConfirm = async () => {
    if (!previewData || !valuationDate || !clientId) return;
    setConfirming(true);
    try {
      const confirmCurves = previewData.curves.map(c => ({
        pairKey: c.pairKey,
        tenorPoints: c.tenorPoints,
      }));

      const response = await valuationService.confirmFwdPointCurves(clientId, {
        valuationDate,
        curves: confirmCurves,
      });

      setUploadResult(response.data);
      setToast({
        message: t('valuation.fwdPointCurves.uploadSuccess', { count: response.data.curvesUploaded }),
        type: 'success',
      });

      setSelectedFile(null);
      setPreviewData(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      setFilterDate(valuationDate);
      loadAllCurves();
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.fwdPointCurves.uploadError'), type: 'error' });
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewData || !valuationDate || !clientId) return;

    // Check if any curves already exist for this date
    try {
      const existing = await valuationService.getFwdPointCurves(clientId, valuationDate);
      if (existing.data && existing.data.length > 0) {
        const existingPairs = existing.data.map(c => c.pairKey).join(', ');
        setOverwriteModal({ pairs: existingPairs, date: formatDate(valuationDate) });
        return;
      }
    } catch {
      // If check fails, proceed anyway
    }

    executeConfirm();
  };

  // ============================================
  // View curve detail
  // ============================================

  const handleViewCurve = async (pairKey: string, date: string) => {
    if (!clientId) return;
    setLoadingCurve(true);
    try {
      const response = await valuationService.getFwdPointCurve(clientId, pairKey, date);
      setSelectedCurve(response.data);
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.fwdPointCurves.loadError'), type: 'error' });
    } finally {
      setLoadingCurve(false);
    }
  };

  // ============================================
  // Delete curve
  // ============================================

  const handleDeleteCurve = (pairKey: string, date: string) => {
    setDeleteModal({ pairKey, date });
  };

  const executeDelete = async () => {
    if (!deleteModal || !clientId) return;
    const { pairKey, date } = deleteModal;
    setDeleteModal(null);

    try {
      await valuationService.deleteFwdPointCurve(clientId, pairKey, date);
      setToast({
        message: t('valuation.fwdPointCurves.deleteSuccess', { pairKey, date: formatDate(date) }),
        type: 'success',
      });
      if (selectedCurve?.pairKey === pairKey && selectedCurve?.valuationDate === date) {
        setSelectedCurve(null);
      }
      loadAllCurves();
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.fwdPointCurves.deleteError'), type: 'error' });
    }
  };

  // ============================================
  // Chart options (bid + ask lines)
  // ============================================

  const chartOptions = useMemo(() => {
    if (!selectedCurve || !selectedCurve.tenorPoints?.length) return null;
    const points = selectedCurve.tenorPoints;

    // Spot anchor: forward points are always 0 at spot (Day 0) by convention
    const spotLabel = t('valuation.fwdPointCurves.spotAnchorLabel', 'Spot★');
    const categories = [spotLabel, ...points.map(p => p.tenorLabel)];

    const allValues = [0, ...points.flatMap(p => [p.bidPoints, p.askPoints])];
    const minVal = Math.min(...allValues);
    const maxVal = Math.max(...allValues);
    const range = maxVal - minVal;
    const padding = range > 0 ? range * 0.2 : Math.abs(minVal) * 0.1 + 1;

    const spotAnchorPoint = { value: 0, symbol: 'diamond', symbolSize: 10, itemStyle: { color: '#ff9800' } };

    return {
      title: {
        text: `${selectedCurve.pairKey} — ${formatDate(selectedCurve.valuationDate)}`,
        textStyle: { color: '#ffffff', fontSize: 14, fontWeight: 500 },
        left: 'center',
      },
      tooltip: {
        trigger: 'axis' as const,
        formatter: (params: any) => {
          const arr = Array.isArray(params) ? params : [params];
          const idx = arr[0]?.dataIndex ?? 0;
          if (idx === 0) {
            return `<b>${spotLabel}</b><br/>${t('valuation.fwdPointCurves.daysLabel')}: 0<br/>${t('valuation.fwdPointCurves.bidLabel')}: 0<br/>${t('valuation.fwdPointCurves.askLabel')}: 0<br/><i style="color:#ff9800">${t('valuation.fwdPointCurves.spotAnchorNote', 'Fixed at zero by convention')}</i>`;
          }
          const p = points[idx - 1];
          if (!p) return '';
          return `<b>${p.tenorLabel}</b><br/>${t('valuation.fwdPointCurves.daysLabel')}: ${p.days}<br/>${t('valuation.fwdPointCurves.bidLabel')}: ${p.bidPoints}<br/>${t('valuation.fwdPointCurves.askLabel')}: ${p.askPoints}`;
        },
      },
      legend: {
        data: ['Bid', 'Ask'],
        textStyle: { color: '#cccccc' },
        bottom: 0,
      },
      grid: { left: '12%', right: '5%', bottom: '20%', top: '15%' },
      xAxis: {
        type: 'category' as const,
        data: categories,
        axisLabel: { color: '#cccccc', rotate: 45, fontSize: 11 },
        axisLine: { lineStyle: { color: '#444444' } },
      },
      yAxis: {
        type: 'value' as const,
        name: t('valuation.fwdPointCurves.pointsAxis'),
        nameTextStyle: { color: '#cccccc' },
        min: minVal - padding,
        max: maxVal + padding,
        axisLabel: { color: '#cccccc', formatter: (val: number) => val.toFixed(2) },
        axisLine: { lineStyle: { color: '#444444' } },
        splitLine: { lineStyle: { color: '#333333' } },
      },
      series: [
        {
          name: 'Bid',
          type: 'line' as const,
          data: [spotAnchorPoint, ...points.map(p => p.bidPoints)],
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: '#ef5350', width: 2 },
          itemStyle: { color: '#ef5350' },
        },
        {
          name: 'Ask',
          type: 'line' as const,
          data: [spotAnchorPoint, ...points.map(p => p.askPoints)],
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: '#4fc3f7', width: 2 },
          itemStyle: { color: '#4fc3f7' },
        },
      ],
    };
  }, [selectedCurve, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <div className="accounting-tab-content">
      {/* Upload Section */}
      <div className="config-section" style={{ marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
          {t('valuation.fwdPointCurves.uploadTitle')}
        </h3>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch' }}>
          {/* Valuation Date */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: '160px' }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {t('valuation.fwdPointCurves.valuationDate')}
            </label>
            <input
              type="date"
              value={valuationDate}
              onChange={(e) => setValuationDate(e.target.value)}
              style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '0.4rem 0.6rem',
                color: 'var(--text-primary)',
                fontSize: '0.85rem',
              }}
            />
          </div>

          {/* Drag-and-drop file zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{
              border: '2px dashed var(--border-color)',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              textAlign: 'center',
              cursor: previewLoading ? 'wait' : 'pointer',
              transition: 'border-color 0.2s',
              flex: '1 1 200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            {previewLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: 18, height: 18,
                  border: '2px solid var(--border-color)',
                  borderTopColor: 'var(--accent-blue)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {t('valuation.fwdPointCurves.previewing')}
                </span>
              </div>
            ) : selectedFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                  {selectedFile.name}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); clearFile(); }}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-secondary)',
                    borderRadius: '3px',
                    padding: '0.1rem 0.4rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('valuation.fwdPointCurves.clearFile')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>&#128196;</span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                  {t('valuation.fwdPointCurves.dragDrop')}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  ({t('valuation.fwdPointCurves.acceptedFormats')})
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Format note */}
        <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          {t('valuation.fwdPointCurves.formatNote')}
        </div>

        {/* Preview Panel */}
        {previewData && previewData.curves.length > 0 && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
              {t('valuation.fwdPointCurves.previewTitle')}
            </h4>

            {/* Global warnings */}
            {previewData.warnings.length > 0 && (
              <div style={{ marginBottom: '0.5rem', color: '#ff9800', fontSize: '0.8rem' }}>
                {previewData.warnings.map((w, i) => (
                  <div key={i}>&#9888; {w}</div>
                ))}
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {t('valuation.fwdPointCurves.pairKey')}
                  </th>
                  <th style={{ textAlign: 'center', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {t('valuation.fwdPointCurves.points')}
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {t('valuation.fwdPointCurves.tenorValues')}
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {t('valuation.fwdPointCurves.warningsLabel')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {previewData.curves.map((curve) => (
                  <tr key={curve.pairKey} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600 }}>{curve.pairKey}</td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>{curve.tenorPointCount}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.15rem 0.75rem',
                        fontSize: '0.8rem',
                        color: 'var(--text-secondary)',
                        fontFamily: 'monospace',
                      }}>
                        {curve.tenorPoints.map((tp, i) => (
                          <span key={i}>
                            <span style={{ color: 'var(--text-primary)' }}>{tp.tenorLabel}</span>:&nbsp;
                            <span style={{ color: '#ef5350' }}>{tp.bidPoints}</span>/
                            <span style={{ color: '#4fc3f7' }}>{tp.askPoints}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', fontSize: '0.8rem', color: '#ff9800' }}>
                      {curve.warnings.length > 0
                        ? curve.warnings.map((w, i) => <div key={i}>&#9888; {w}</div>)
                        : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Parse errors */}
            {previewData.errors.length > 0 && (
              <div style={{ marginBottom: '0.75rem', color: '#ff9800', fontSize: '0.8rem' }}>
                {t('valuation.fwdPointCurves.errorsCount', { count: previewData.errors.length })}
                {previewData.errors.map((e, i) => (
                  <div key={i} style={{ marginLeft: '1rem' }}>
                    {e.pairKey ? `${e.pairKey}: ` : ''}{e.error}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleConfirm}
                disabled={confirming}
                style={{
                  background: confirming ? 'var(--bg-secondary)' : '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: confirming ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {confirming ? t('valuation.fwdPointCurves.confirming') : t('valuation.fwdPointCurves.confirmUpload')}
              </button>
              <button
                onClick={clearFile}
                disabled={confirming}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: confirming ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.fwdPointCurves.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Upload result summary */}
        {uploadResult && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: uploadResult.errors.length > 0 ? 'rgba(255,152,0,0.1)' : 'rgba(76,175,80,0.1)',
            border: `1px solid ${uploadResult.errors.length > 0 ? 'rgba(255,152,0,0.3)' : 'rgba(76,175,80,0.3)'}`,
            borderRadius: '4px',
            fontSize: '0.85rem',
          }}>
            <div>
              {t('valuation.fwdPointCurves.uploadedSummary', {
                count: uploadResult.curvesUploaded,
                date: formatDate(uploadResult.valuationDate),
              })}
            </div>
            {uploadResult.curves.map((c, i) => (
              <div key={i} style={{ marginLeft: '1rem', color: 'var(--text-secondary)' }}>
                {c.pairKey}: {c.tenorPoints} {t('valuation.fwdPointCurves.tenorPointsLabel')}
              </div>
            ))}
            {uploadResult.errors.length > 0 && (
              <div style={{ marginTop: '0.5rem', color: '#ff9800' }}>
                {t('valuation.fwdPointCurves.errorsCount', { count: uploadResult.errors.length })}
                {uploadResult.errors.map((e, i) => (
                  <div key={i} style={{ marginLeft: '1rem' }}>{e.pairKey ? `${e.pairKey}: ` : ''}{e.error}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Available Curves Section */}
      <div className="config-section" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
            {t('valuation.fwdPointCurves.availableCurves')}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {t('valuation.fwdPointCurves.filterDate')}:
            </label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '0.3rem 0.5rem',
                color: 'var(--text-primary)',
                fontSize: '0.8rem',
              }}
            />
            {filterDate && (
              <button
                onClick={() => setFilterDate('')}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '3px',
                  padding: '0.2rem 0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                }}
              >
                {t('valuation.fwdPointCurves.clearFilter')}
              </button>
            )}
          </div>
        </div>

        {/* Date chips */}
        {availableDates.length > 0 && (
          <div style={{ marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginRight: '0.25rem' }}>
              {t('valuation.fwdPointCurves.availableDates')}
            </span>
            {availableDates.map((date) => (
              <button
                key={date}
                onClick={() => setFilterDate(filterDate === date ? '' : date)}
                style={{
                  display: 'inline-block',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  border: filterDate === date ? '1px solid var(--accent-blue)' : '1px solid var(--border-color)',
                  background: filterDate === date ? 'var(--accent-blue)' : 'var(--bg-tertiary, var(--bg-secondary))',
                  color: filterDate === date ? '#ffffff' : 'var(--text-secondary)',
                  transition: 'all 0.15s',
                }}
              >
                {formatDate(date)}
              </button>
            ))}
          </div>
        )}

        {loadingCurves ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
            {t('common.loading')}...
          </div>
        ) : filteredCurves.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
            {t('valuation.fwdPointCurves.noCurves')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {groupedCurves.map(([pairKey, curves]) => {
              const isCollapsed = collapsedSections.has(pairKey);
              return (
                <div key={pairKey}>
                  <button
                    onClick={() => toggleSection(pairKey)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      background: '#252525',
                      border: '1px solid #333',
                      borderRadius: isCollapsed ? '8px' : '8px 8px 0 0',
                      cursor: 'pointer',
                      color: 'var(--text-primary)',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', width: '12px' }}>
                      {isCollapsed ? '▶' : '▼'}
                    </span>
                    <span>{pairKey}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                      ({curves.length} {curves.length === 1 ? t('valuation.fwdPointCurves.curveOne') : t('valuation.fwdPointCurves.curveMany')})
                    </span>
                  </button>

                  {!isCollapsed && (
                    <div style={{
                      background: '#1a1a1a',
                      border: '1px solid #333',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      padding: '0.25rem 0',
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {t('valuation.fwdPointCurves.valuationDate')}
                            </th>
                            <th style={{ textAlign: 'center', padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {t('valuation.fwdPointCurves.points')}
                            </th>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {t('valuation.fwdPointCurves.uploaded')}
                            </th>
                            <th style={{ textAlign: 'center', padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {t('valuation.fwdPointCurves.actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {curves.map((curve, idx) => (
                            <tr key={`${curve.valuationDate}-${idx}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td style={{ padding: '0.4rem 0.75rem' }}>{formatDate(curve.valuationDate)}</td>
                              <td style={{ padding: '0.4rem 0.75rem', textAlign: 'center' }}>{curve.tenorPoints}</td>
                              <td style={{ padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                {formatDateTime(curve.uploadedAt)}
                              </td>
                              <td style={{ padding: '0.4rem 0.75rem', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleViewCurve(curve.pairKey, curve.valuationDate)}
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid var(--accent-blue)',
                                    color: 'var(--accent-blue)',
                                    borderRadius: '3px',
                                    padding: '0.2rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    marginRight: '0.5rem',
                                  }}
                                >
                                  {t('valuation.fwdPointCurves.view')}
                                </button>
                                <button
                                  onClick={() => handleDeleteCurve(curve.pairKey, curve.valuationDate)}
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid #ef5350',
                                    color: '#ef5350',
                                    borderRadius: '3px',
                                    padding: '0.2rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {t('valuation.fwdPointCurves.delete')}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Curve Visualization Panel */}
      {selectedCurve && (
        <div className="config-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
              {selectedCurve.pairKey} — {formatDate(selectedCurve.valuationDate)}
            </h3>
            <button
              onClick={() => setSelectedCurve(null)}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-color)',
                color: 'var(--text-secondary)',
                borderRadius: '3px',
                padding: '0.2rem 0.6rem',
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              {t('valuation.fwdPointCurves.close')}
            </button>
          </div>

          {loadingCurve ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              {t('common.loading')}...
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* Chart */}
              <div style={{ flex: '2 1 400px', minWidth: 0 }}>
                {chartOptions && (
                  <ReactEcharts
                    option={chartOptions}
                    style={{ height: '300px', width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                  />
                )}
              </div>

              {/* Tenor points table */}
              <div style={{ flex: '1 1 280px', maxWidth: '380px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {t('valuation.fwdPointCurves.tenor')}
                      </th>
                      <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {t('valuation.fwdPointCurves.daysLabel')}
                      </th>
                      <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#ef5350', fontWeight: 500 }}>
                        {t('valuation.fwdPointCurves.bidLabel')}
                      </th>
                      <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#4fc3f7', fontWeight: 500 }}>
                        {t('valuation.fwdPointCurves.askLabel')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Spot anchor: forward points = 0 at spot (Day 0) by convention */}
                    <tr style={{ borderBottom: '1px solid var(--border-color)', background: 'rgba(255, 152, 0, 0.07)' }}>
                      <td style={{ padding: '0.4rem 0.5rem', color: '#ff9800', fontStyle: 'italic' }}>
                        {t('valuation.fwdPointCurves.spotAnchorLabel', 'Spot★')}
                      </td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', color: '#ff9800' }}>0</td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', color: '#ff9800' }}>0</td>
                      <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', color: '#ff9800' }}>0</td>
                    </tr>
                    {selectedCurve.tenorPoints.map((point, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.4rem 0.5rem' }}>{point.tenorLabel}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace' }}>{point.days}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', color: '#ef5350' }}>
                          {point.bidPoints}
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', color: '#4fc3f7' }}>
                          {point.askPoints}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Overwrite Confirmation Modal */}
      {overwriteModal && (
        <div
          onClick={() => setOverwriteModal(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('valuation.fwdPointCurves.overwriteTitle')}
            </h3>
            <p style={{ margin: '0 0 1.25rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('valuation.fwdPointCurves.overwriteConfirm', {
                pairs: overwriteModal.pairs,
                date: overwriteModal.date,
              })}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setOverwriteModal(null)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.fwdPointCurves.cancel')}
              </button>
              <button
                onClick={() => { setOverwriteModal(null); executeConfirm(); }}
                style={{
                  background: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.fwdPointCurves.overwriteButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div
          onClick={() => setDeleteModal(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('valuation.fwdPointCurves.deleteTitle')}
            </h3>
            <p style={{ margin: '0 0 1.25rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('valuation.fwdPointCurves.deleteConfirm', {
                pairKey: deleteModal.pairKey,
                date: formatDate(deleteModal.date),
              })}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteModal(null)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.fwdPointCurves.cancel')}
              </button>
              <button
                onClick={executeDelete}
                style={{
                  background: '#ef5350',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.fwdPointCurves.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ValuationFwdPointCurves;
